import { RewardProduct_Redirect } from '__generated__/graphql';

const VALID_SOURCES = [
  'affiliate',
  'editorial-page',
  'extra1',
  'extra2',
  'product-page',
  'result-page',
];

/**
 * Generates a URL based on the provided source and product details.
 *
 * @param source - The source type which determines the URL to be returned.
 *                 Possible values include 'affiliate', 'editorial-page', 'extra1', 'extra2', 'product-page' and 'result-page.
 * @param product - An object of type `RewardProduct_Redirect` containing various URL properties.
 * @param useSecondUrlSet - When true, resolve against the 2nd URL set used by the
 *                 V3 form's "No thanks, just apply for card" journey (CMS-1798).
 *                 Each 2nd field falls back to its 1st-set counterpart when empty.
 *
 * @returns The URL corresponding to the provided source. If the source is not found in the map,
 *          it returns the `urlResultPage` from the product object. If `urlResultPage` is also not available,
 *          it returns an empty string.
 */
export function getUrlBasedOnSource(
  source: string,
  product: RewardProduct_Redirect,
  useSecondUrlSet = false,
): string {
  const bySource: Record<string, { first?: string; second?: string }> = {
    affiliate: { first: product.urlAffiliate, second: product.urlAffiliate2 },
    'editorial-page': {
      first: product.urlEditorialPage,
      second: product.urlEditorialPage2,
    },
    extra1: { first: product.urlExtra1, second: product.urlExtra12 },
    extra2: { first: product.urlExtra2, second: product.urlExtra22 },
    'product-page': {
      first: product.urlProductPage,
      second: product.urlProductPage2,
    },
    'result-page': {
      first: product.urlResultPage,
      second: product.urlResultPage2,
    },
  };
  const entry = bySource[source];

  if (useSecondUrlSet) {
    // "No thanks, just apply for card" journey: prefer the 2nd URL set, falling
    // back per-field to the 1st when the 2nd field is empty/unset (Scenario 5).
    // The whole cascade uses `||` (not `??`) so an empty '' field falls through
    // — apply-v3 sends no `source`, so an empty 2nd result page must still fall
    // back to the 1st result page rather than yield an empty redirect.
    return (
      (entry?.second ||
        entry?.first ||
        product.urlResultPage2 ||
        product.urlResultPage) ??
      ''
    );
  }

  return entry?.first ?? product.urlResultPage ?? '';
}

/**
 * Returns a valid source type based on the provided source.
 *
 * @param source - The source type to be validated.
 *
 * @returns The source type if it is valid, otherwise an empty string.
 */
export function getValidSource(source: string | null | undefined): string {
  return source && VALID_SOURCES.includes(source) ? source : '';
}

type AvailableMacro = {
  utm_campaign?: string;
  utm_medium?: string;
  utm_source?: string;
  clkid?: string;
  ga_action?: string;
  ga_label?: string;
  ga_category?: string;
  loan_amount?: string;
  loan_time?: string;
  loan_time_unit?: string;
  criteria?: string;
};

export function macrosUri(
  url: string,
  args: AvailableMacro = {},
  urlParams?: URLSearchParams,
): string {
  const availableMacros: Record<string, string> = {
    utm_campaign: '{utm_campaign}',
    utm_medium: '{utm_medium}',
    utm_source: '{utm_source}',
    clkid: '{clkid}',
    ga_action: '{ga_action}',
    ga_label: '{ga_label}',
    ga_category: '{ga_category}',
    loan_amount: '{loan_amount}',
    loan_time: '{loan_time}',
    loan_time_unit: '{loan_time_unit}',
    criteria: '{criteria}',
  };

  const replaceMacro = (macroKey: string, macroValue: string) => {
    // Check if the URL contains the macro placeholder and replace it with the value
    if (url.includes(macroValue)) {
      const value =
        args[macroKey as keyof AvailableMacro] ||
        urlParams?.get(macroKey) ||
        '';
      url = url.replace(macroValue, value ? encodeURIComponent(value) : '');
    }
  };

  // Loop over available macros and replace them
  Object.keys(availableMacros).forEach((macroKey) => {
    const macroValue = availableMacros[macroKey];
    replaceMacro(macroKey, macroValue);
  });

  return url;
}

type DeepLinkKey =
  | 'productId'
  | 'payloadCriteria'
  | 'productTitle'
  | 'forwardParameters';

// DeepLink parameters to replace
const deepLinkParameters: Record<DeepLinkKey, string> = {
  productId: '[payloadProductId]',
  payloadCriteria: '[payloadCriteria]',
  productTitle: '[productTitle]',
  forwardParameters: '[forwardParameters]',
};

// Per-key encoding policy, owned here so the server (redirect/page.tsx) and
// client (RedirectScreen) callers cannot drift apart. `productId`,
// `payloadCriteria` and `productTitle` are single opaque values that may carry
// reserved chars or spaces (e.g. a title like `Term Insurance Plus`), so they
// are percent-encoded to survive as one query value. `forwardParameters` is
// intentionally left raw: it is a ready-made query fragment (e.g.
// `channel=web&step=1`) whose `&`/`=` must splice live into the provider URL.
const deepLinkEncoded: Record<DeepLinkKey, boolean> = {
  productId: true,
  payloadCriteria: true,
  productTitle: true,
  forwardParameters: false,
};

export const deepLinkParamReplacement = (
  ctaUrl: string,
  values: Partial<Record<DeepLinkKey, string>>,
): string => {
  // Loop over available deep-link tokens and replace them. Callers pass raw
  // values; encoding is applied here per `deepLinkEncoded`.
  Object.keys(deepLinkParameters).forEach((key) => {
    const typedKey = key as DeepLinkKey;
    const anchor = deepLinkParameters[typedKey];
    // A `new URL(...).toString()` round-trip (e.g. after `searchParams.set()`
    // in the client redirect flow) percent-encodes the `[` / `]` to `%5B` /
    // `%5D`, so the literal token no longer matches. Match both forms.
    const encodedAnchor = encodeURIComponent(anchor);
    const rawValue = values[typedKey] ?? '';
    const value = deepLinkEncoded[typedKey]
      ? encodeURIComponent(rawValue)
      : rawValue;
    // Pass the value via a replacer function so `$` sequences in it (e.g. `$&`,
    // `$'`, `$1`) are inserted literally rather than interpreted by `replace`.
    const replacer = () => value;

    if (ctaUrl.includes(anchor)) {
      ctaUrl = ctaUrl.replace(anchor, replacer);
    } else if (ctaUrl.includes(encodedAnchor)) {
      ctaUrl = ctaUrl.replace(encodedAnchor, replacer);
    }
  });

  return ctaUrl;
};
